/* ============================================================================
 * Yakob & Rahel Maison — theme.js
 * Vanilla JS, single IIFE. Cart drawer, express checkout, motion, focus traps,
 * mobile sticky CTA. Wired to Shopify Cart Ajax API.
 * ========================================================================== */
(function () {
  'use strict';

  // ---------- 1. Utilities --------------------------------------------------

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function moneyFormat(cents) {
    const fmt = (window.YR && window.YR.money_format) || '${{amount}}';
    const amount = (Number(cents) / 100);
    const placeholders = {
      '{{amount}}': amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      '{{amount_no_decimals}}': Math.round(amount).toLocaleString(),
      '{{amount_with_comma_separator}}': amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      '{{amount_no_decimals_with_comma_separator}}': Math.round(amount).toLocaleString('de-DE'),
      '{{amount_with_apostrophe_separator}}': amount.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      '{{amount_no_decimals_with_space_separator}}': Math.round(amount).toLocaleString('fr-FR'),
      '{{amount_with_space_separator}}': amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      '{{amount_with_period_and_space_separator}}': amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    };
    let out = fmt;
    Object.keys(placeholders).forEach((k) => { out = out.split(k).join(placeholders[k]); });
    return out;
  }

  const roman = (n) => {
    const map = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    return map[n] || String(n);
  };

  const escapeHTML = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // ---------- 2. Cart store -------------------------------------------------

  const cart = {
    state: (window.YR && window.YR.cart) || { item_count: 0, items: [], total_price: 0 },

    async refresh() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('cart.js ' + res.status);
        const data = await res.json();
        this.state = data;
        this.render();
        this.emitCountUpdate();
        return data;
      } catch (err) {
        console.warn('[YR cart] refresh failed', err);
      }
    },

    async add(variantId, qty, properties) {
      const payload = { id: Number(variantId), quantity: Number(qty || 1) };
      if (properties && Object.keys(properties).length) payload.properties = properties;
      const body = JSON.stringify(payload);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to tin box');
      }
      await this.refresh();
      openDrawer();
      return res.json().catch(() => null);
    },

    async change(lineId, qty) {
      // lineId is the line-item KEY ("variantId:hash") for lines with properties,
      // or a plain variant id otherwise. Pass it through as-is — Shopify's
      // /cart/change.js rejects Number() on keys (they contain a colon).
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineId, quantity: Number(qty) })
      });
      if (!res.ok) throw new Error('cart/change failed');
      const data = await res.json();
      this.state = data;
      this.render();
      this.emitCountUpdate();
      return data;
    },

    async remove(variantId) {
      return this.change(variantId, 0);
    },

    render() {
      const body = $('[data-cart-body]');
      const footer = $('[data-cart-footer]');
      const subEl = $('[data-cart-subtotal]');
      if (!body) return;

      const items = (this.state && this.state.items) || [];

      if (!items.length) {
        body.innerHTML = `
          <div class="cart-empty" role="status">
            <div class="cart-empty__monogram" aria-hidden="true">&amp;</div>
            <p class="cart-empty__heading">The tin box is empty.</p>
            <p class="cart-empty__sub">Every love story begins with the first page.</p>
            <a class="cart-empty__cta" href="/collections">&rarr; Open the four chapters</a>
          </div>`;
        if (footer) footer.setAttribute('data-empty', 'true');
        if (subEl) subEl.textContent = moneyFormat(0);
        return;
      }

      if (footer) footer.removeAttribute('data-empty');

      // Each chapter's tin carries its own jewel ground — the gold mascot reads as
      // it does on the real tin, never washed-out on flat cream.
      const TIN_BG = ['var(--stage-bloom)', 'var(--stage-longing)', 'var(--stage-rooted)', 'var(--stage-being)'];

      const rows = items.map((it, idx) => {
        const chapter = (it.properties && it.properties._chapter) || (idx + 1);
        const chNum = Number(chapter) || (idx + 1);
        const mascotSrc = it.properties && it.properties._mascot;
        const tinBg = TIN_BG[(chNum - 1) % TIN_BG.length] || 'var(--baroque-deep)';
        let thumb;
        if (mascotSrc) {
          // Branded tin tile — gold mascot on the chapter's jewel ground
          thumb = `<div class="cart-line__thumb cart-line__thumb--tin" style="--tin-bg:${tinBg}" aria-hidden="true"><img src="${escapeHTML(mascotSrc)}" alt="" loading="lazy"></div>`;
        } else if (it.image) {
          thumb = `<div class="cart-line__thumb cart-line__thumb--photo"><img src="${escapeHTML(it.image)}" alt="${escapeHTML(it.product_title)}" loading="lazy"></div>`;
        } else {
          thumb = `<div class="cart-line__thumb cart-line__thumb--placeholder" aria-hidden="true">&amp;</div>`;
        }
        return `
          <article class="cart-line" data-cart-line data-variant-id="${it.variant_id}" data-line-key="${it.key}">
            ${thumb}
            <div class="cart-line__detail">
              <h3 class="cart-line__title">${escapeHTML(it.product_title)}</h3>
              <p class="cart-line__meta">Chapter ${roman(chNum)}${it.variant_title && it.variant_title !== 'Default Title' ? ' &middot; ' + escapeHTML(it.variant_title) : ''}</p>
              <div class="cart-line__qty" role="group" aria-label="Quantity for ${escapeHTML(it.product_title)}">
                <button type="button" class="cart-line__qty-btn" data-cart-decrement aria-label="Decrease quantity">&minus;</button>
                <span class="cart-line__qty-count" data-cart-qty aria-live="polite" aria-atomic="true">${it.quantity}</span>
                <button type="button" class="cart-line__qty-btn" data-cart-increment aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div class="cart-line__side">
              <p class="cart-line__price">${moneyFormat(it.final_line_price)}</p>
              <button type="button" class="cart-line__remove" data-cart-remove>Remove</button>
            </div>
          </article>`;
      }).join('');

      body.innerHTML = rows;
      if (subEl) subEl.textContent = moneyFormat(this.state.total_price || 0);
    },

    emitCountUpdate() {
      const count = (this.state && this.state.item_count) || 0;
      // Update display elements (digit only, no parentheses) — matches §6 spec
      $$('[data-cart-count-display]').forEach((el) => {
        el.textContent = String(count);
      });
      // Update attribute-only count markers + trigger the 320ms ampersand pulse
      $$('[data-cart-count]').forEach((el) => {
        const prev = Number(el.getAttribute('data-count') || '0');
        el.setAttribute('data-count', String(count));
        if (count > prev) {
          el.setAttribute('data-pulse', 'true');
          setTimeout(() => el.removeAttribute('data-pulse'), 360);
        }
      });
    }
  };

  // ---------- 2b. Modal focus management (traps + page inert) ---------------

  const FOCUSABLE_SELECTOR = [
    'a[href]', 'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])',
    'button:not([disabled])', 'iframe', 'object', 'embed',
    'audio[controls]', 'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  // Visible focusable descendants of a container, in DOM order.
  function getFocusable(container) {
    if (!container) return [];
    return $$(FOCUSABLE_SELECTOR, container).filter((el) => {
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
      if (el.closest('[inert]')) return false;
      // Rendered check — excludes display:none / detached / zero-box elements.
      return el.getClientRects().length > 0;
    });
  }

  // Make every top-level element inert (removed from tab order + a11y tree)
  // except the ones we want to keep interactive (the open drawer + its overlay).
  function activatePageInert(keep) {
    Array.prototype.forEach.call(document.body.children, (el) => {
      if (keep.some((k) => k && (el === k || el.contains(k)))) return;
      if (el.hasAttribute('inert')) return;
      el.setAttribute('inert', '');
      el.setAttribute('data-yr-inert', '');
    });
  }

  function releasePageInert() {
    $$('[data-yr-inert]').forEach((el) => {
      el.removeAttribute('inert');
      el.removeAttribute('data-yr-inert');
    });
  }

  // Tab/Shift+Tab cycle keeper. getContainer() is resolved lazily so the trap
  // survives the drawer body being re-rendered by cart.render().
  function createFocusTrap(getContainer) {
    let active = false;
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      const container = getContainer();
      if (!container) return;
      const items = getFocusable(container);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (e.shiftKey) {
        if (current === first || !container.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    }
    return {
      activate() {
        if (active) return;
        active = true;
        document.addEventListener('keydown', onKeydown, true);
      },
      deactivate() {
        if (!active) return;
        active = false;
        document.removeEventListener('keydown', onKeydown, true);
      }
    };
  }

  // ---------- 3. Drawer open/close ------------------------------------------

  let lastFocusedBeforeDrawer = null;
  const cartTrap = createFocusTrap(() => $('[data-cart-drawer]'));

  function openDrawer() {
    const drawer = $('[data-cart-drawer]');
    const overlay = $('[data-cart-overlay]');
    if (!drawer) return;
    lastFocusedBeforeDrawer = document.activeElement;
    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    document.documentElement.setAttribute('data-cart-drawer-open', 'true');
    // Keep the drawer + its overlay interactive; inert the rest of the page so
    // AT and Tab cannot reach the content behind the modal.
    activatePageInert([drawer, overlay]);
    cartTrap.activate();
    const closeBtn = $('[data-cart-close]', drawer);
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    const drawer = $('[data-cart-drawer]');
    const overlay = $('[data-cart-overlay]');
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    document.documentElement.removeAttribute('data-cart-drawer-open');
    cartTrap.deactivate();
    releasePageInert();
    if (lastFocusedBeforeDrawer && typeof lastFocusedBeforeDrawer.focus === 'function') {
      lastFocusedBeforeDrawer.focus();
    }
  }

  // ---------- 4. Global click handler ---------------------------------------

  // Defensive: bind close directly on the button + overlay too, so closing
  // works even if the delegated handler below is ever interrupted (rogue
  // stopPropagation elsewhere on the page, etc.). The close button is static
  // markup rendered once, so a single bind on load is sufficient.
  function bindDirectClose() {
    document.querySelectorAll('[data-cart-close], [data-cart-overlay]').forEach((el) => {
      if (el.dataset.closeBound === '1') return;
      el.dataset.closeBound = '1';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeDrawer();
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindDirectClose);
  } else {
    bindDirectClose();
  }

  document.addEventListener('click', async (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (t.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (t.closest('[data-cart-close]') || t.closest('[data-cart-overlay]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Quick add-to-box (e.g. the /collections rolodex rows)
    const addBtn = t.closest('[data-add-to-cart]');
    if (addBtn) {
      e.preventDefault();
      const vid = addBtn.getAttribute('data-variant-id');
      if (!vid || addBtn.dataset.busy === '1') return;
      addBtn.dataset.busy = '1';
      // Carry the chapter + mascot through as hidden line properties so the
      // cart drawer shows the right chapter number and a branded tin thumbnail
      // (these products have no photographs by design).
      const props = {};
      const ch = addBtn.getAttribute('data-chapter');
      const mascotSrc = addBtn.getAttribute('data-mascot-src');
      if (ch) props._chapter = ch;
      if (mascotSrc) props._mascot = mascotSrc;
      const labelEl = addBtn.querySelector('.rolodex__add-label') || addBtn;
      const original = labelEl.textContent;
      labelEl.textContent = 'Adding…';
      try {
        const addQty = Number(addBtn.getAttribute('data-quantity')) || 1;
        await cart.add(vid, addQty, props); // refreshes + opens the drawer
        labelEl.textContent = 'Added ✓';
      } catch (err) {
        console.warn('[YR] quick add failed', err);
        labelEl.textContent = 'Unavailable';
      }
      setTimeout(function () {
        labelEl.textContent = original;
        addBtn.dataset.busy = '0';
      }, 1800);
      return;
    }

    const line = t.closest('[data-cart-line]');
    if (!line) return;
    // Prefer the line-item key (required for lines with _chapter/_mascot properties);
    // fall back to the variant id for plain lines.
    const variantId = line.getAttribute('data-line-key') || line.getAttribute('data-variant-id');
    const qtyEl = $('[data-cart-qty]', line);
    const currentQty = qtyEl ? Number(qtyEl.textContent) : 1;

    if (t.closest('[data-cart-increment]')) {
      e.preventDefault();
      qtyEl && qtyEl.classList.add('is-flash');
      setTimeout(() => qtyEl && qtyEl.classList.remove('is-flash'), 600);
      try { await cart.change(variantId, currentQty + 1); } catch (err) { console.warn(err); }
    } else if (t.closest('[data-cart-decrement]')) {
      e.preventDefault();
      try { await cart.change(variantId, Math.max(0, currentQty - 1)); } catch (err) { console.warn(err); }
    } else if (t.closest('[data-cart-remove]')) {
      e.preventDefault();
      try { await cart.remove(variantId); } catch (err) { console.warn(err); }
    }
  });

  // ---------- 5. ESC closes drawer ------------------------------------------

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const drawer = $('[data-cart-drawer]');
      if (drawer && drawer.getAttribute('data-open') === 'true') {
        e.preventDefault();
        closeDrawer();
      }
    }
  });

  // ---------- 6. Product page add-to-cart interceptor -----------------------

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('action') !== '/cart/add' && !form.matches('form[action*="/cart/add"]')) return;

    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const originalText = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.innerHTML = 'Sealing&hellip;'; }

    try {
      const fd = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to tin box');
      }
      // Wax-seal success state per §8 line 887 — 720ms scale 0 → 1.06 → 1.00
      if (btn) {
        btn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:0.45em;">' +
          '<svg width="18" height="18" viewBox="0 0 100 100" aria-hidden="true" focusable="false" style="vertical-align:middle;transform:scale(0);animation:yr-wax-seal-in 720ms cubic-bezier(0.22,1,0.36,1) forwards;">' +
          '<circle cx="50" cy="50" r="42" fill="#6B1818"/>' +
          '<text x="50" y="62" text-anchor="middle" font-family="Fraunces,Georgia,serif" font-size="36" font-style="italic" fill="#D4A651">Y&amp;R</text>' +
          '</svg>' +
          '<span style="font-style:italic;">Sealed</span>' +
          '</span>';
      }
      await cart.refresh();
      // Brief reveal before opening drawer
      setTimeout(() => openDrawer(), 400);
    } catch (err) {
      console.warn('[YR add] failed', err);
      if (btn) btn.innerHTML = 'Could not add &mdash; try again';
      setTimeout(() => { if (btn) btn.innerHTML = originalText; }, 2200);
      return;
    } finally {
      if (btn) {
        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = originalText;
        }, 1400);
      }
    }
  });

  // ---------- 7. Express checkout detection ---------------------------------
  // Pick the single highest-tier express method available on this device.

  function detectExpressMethod() {
    try {
      if (window.ApplePaySession && typeof window.ApplePaySession.canMakePayments === 'function' && window.ApplePaySession.canMakePayments()) {
        return 'apple';
      }
    } catch (_) { /* noop */ }

    try {
      if (window.PaymentRequest) {
        // Heuristic: Google Pay's PaymentRequest is reliably available on Chrome/Android.
        // Full `canMakePayment()` is async; we use UA + presence as a fast synchronous proxy.
        const ua = navigator.userAgent || '';
        const isChromeFamily = /Chrome|CriOS|Android/i.test(ua) && !/Edg\//i.test(ua) === false || /Chrome|CriOS|Android/i.test(ua);
        if (isChromeFamily) return 'google';
      }
    } catch (_) { /* noop */ }

    // Shop Pay is available whenever Shopify is the platform (we are inside a Shopify theme).
    if (window.Shopify) return 'shop';

    return null;
  }

  function expressButtonHTML(method) {
    // 48px-tall platform buttons. The visible label and styling match each
    // payment platform's brand guidelines closely enough for a recognisable
    // CTA; the *real* native sheet is surfaced on Shopify checkout itself.
    const base = 'class="yr-express-btn yr-express-btn--' + method + '" type="button" style="height:48px;min-height:48px;width:100%;display:inline-flex;align-items:center;justify-content:center;gap:0.4em;border:0;border-radius:6px;font-family:-apple-system,BlinkMacSystemFont,system-ui,sans-serif;font-size:15px;font-weight:600;letter-spacing:0.01em;cursor:pointer;padding:0 1.25rem;"';
    if (method === 'apple') {
      return '<button ' + base + ' data-express="apple" aria-label="Buy with Apple Pay" style="height:48px;min-height:48px;width:100%;background:#000;color:#fff;display:inline-flex;align-items:center;justify-content:center;gap:0.4em;border:0;border-radius:6px;cursor:pointer;padding:0 1.25rem;">' +
        '<svg viewBox="0 0 17 20" width="17" height="20" aria-hidden="true" focusable="false" fill="currentColor"><path d="M14.94 6.85c-.1.08-1.86 1.07-1.86 3.28 0 2.56 2.25 3.47 2.32 3.49-.01.06-.36 1.24-1.19 2.45-.74 1.06-1.51 2.13-2.69 2.13s-1.48-.69-2.84-.69c-1.32 0-1.79.71-2.87.71S3.97 17.21 3.1 16.04C2.1 14.66 1.29 12.5 1.29 10.45c0-3.29 2.14-5.04 4.25-5.04 1.12 0 2.05.73 2.76.73.67 0 1.71-.78 2.99-.78.48 0 2.24.04 3.65 1.49zM10.42 4.13c.53-.62.9-1.49.9-2.36 0-.12-.01-.24-.03-.34-.86.03-1.88.57-2.5 1.29-.49.55-.94 1.42-.94 2.3 0 .13.02.27.03.31.05.01.14.02.22.02.77 0 1.74-.52 2.32-1.22z"/></svg>' +
        '<span style="font-size:17px;font-weight:600;letter-spacing:-0.01em;">Pay</span>' +
        '</button>';
    }
    if (method === 'google') {
      return '<button ' + base + ' data-express="google" aria-label="Buy with Google Pay" style="height:48px;min-height:48px;width:100%;background:#000;color:#fff;display:inline-flex;align-items:center;justify-content:center;gap:0.5em;border:0;border-radius:6px;cursor:pointer;padding:0 1.25rem;">' +
        '<svg viewBox="0 0 41 17" width="41" height="17" aria-hidden="true" focusable="false">' +
        '<path fill="#4285F4" d="M19.526 8.674v3.834h-1.218V2.838h3.234c.821 0 1.521.273 2.097.819.585.547.877 1.214.877 2.002 0 .807-.292 1.474-.877 2.014-.567.54-1.265.81-2.097.81h-2.016zm0-4.665v3.498h2.043c.484 0 .887-.164 1.205-.49.324-.327.486-.722.486-1.182 0-.452-.162-.84-.486-1.166-.318-.336-.721-.66-1.205-.66h-2.043z"/>' +
        '<path fill="#EA4335" d="M27.156 5.532c.9 0 1.61.241 2.131.722.521.482.781 1.142.781 1.98v4h-1.165v-.902h-.053c-.504.742-1.174 1.113-2.011 1.113-.713 0-1.31-.211-1.79-.633-.48-.422-.72-.95-.72-1.583 0-.668.252-1.2.757-1.595.504-.395 1.178-.593 2.02-.593.719 0 1.31.132 1.774.395v-.278c0-.422-.167-.781-.5-1.075-.333-.295-.722-.443-1.169-.443-.673 0-1.205.284-1.594.851l-1.073-.674c.578-.832 1.434-1.285 2.612-1.285zm-1.577 4.717c0 .316.134.581.402.793.268.213.582.319.943.319.51 0 .965-.189 1.366-.566.401-.378.601-.821.601-1.33-.376-.298-.901-.447-1.573-.447-.489 0-.897.118-1.224.355-.343.245-.515.535-.515.876z"/>' +
        '<path fill="#FBBC04" d="M40 5.743l-4.806 11.062h-1.257l1.785-3.871-3.165-7.191h1.328l2.286 5.519h.026l2.224-5.519H40z"/>' +
        '<path fill="#34A853" d="M8.18 7.91c0-.395-.035-.774-.1-1.139H4.18v2.155h2.245c-.097.539-.4 1-.842 1.31v1.084h1.366c.797-.736 1.231-1.821 1.231-3.41z"/>' +
        '<path fill="#4285F4" d="M4.18 12.166c1.144 0 2.108-.376 2.811-1.026l-1.366-1.084c-.378.255-.864.41-1.445.41-1.105 0-2.042-.745-2.378-1.748H.397v1.118C1.094 11.224 2.531 12.166 4.18 12.166z"/>' +
        '<path fill="#FBBC04" d="M1.802 8.718c-.087-.255-.135-.527-.135-.81 0-.282.05-.555.135-.81V5.98H.397C.143 6.495 0 7.082 0 7.908s.143 1.413.397 1.928l1.405-1.118z"/>' +
        '<path fill="#EA4335" d="M4.18 4.81c.624 0 1.183.215 1.625.633l1.21-1.21C6.282 3.55 5.32 3.121 4.18 3.121c-1.649 0-3.086.942-3.783 2.33l1.405 1.119C2.138 5.567 3.075 4.81 4.18 4.81z"/>' +
        '</svg>' +
        '</button>';
    }
    if (method === 'shop') {
      return '<button ' + base + ' data-express="shop" aria-label="Buy with Shop Pay" style="height:48px;min-height:48px;width:100%;background:#5A31F4;color:#fff;display:inline-flex;align-items:center;justify-content:center;gap:0.45em;border:0;border-radius:6px;cursor:pointer;padding:0 1.25rem;font-family:-apple-system,BlinkMacSystemFont,system-ui,sans-serif;">' +
        '<span style="font-size:17px;font-weight:700;letter-spacing:-0.01em;font-style:italic;">Shop</span>' +
        '<span style="font-size:17px;font-weight:500;letter-spacing:-0.01em;">Pay</span>' +
        '</button>';
    }
    return '';
  }

  async function expressCheckoutRoute(method) {
    // The 60-second path: add current PDP variant qty=1, route to Shopify's
    // hosted checkout, which surfaces the express sheet automatically.
    const form = document.querySelector('form[action*="/cart/add"]');
    let variantId = null;
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) variantId = idInput.value;
    }
    if (variantId) {
      // Add qty=1 then redirect to /checkout — express button surfaces on Shopify checkout.
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        });
      } catch (err) { console.warn('[YR express] preadd failed', err); }
    }
    window.location.href = '/checkout';
  }

  function initExpressCheckout() {
    const method = detectExpressMethod();
    const mounts = $$('[data-express-checkout], [data-express-checkout-cart]');
    if (!mounts.length) return;
    if (!method) {
      mounts.forEach((m) => { m.innerHTML = ''; });
      return;
    }
    const html = expressButtonHTML(method);
    mounts.forEach((m) => {
      m.innerHTML = html;
      m.setAttribute('data-express-method', method);
    });
    document.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const btn = t.closest('.yr-express-btn');
      if (!btn) return;
      e.preventDefault();
      expressCheckoutRoute(method);
    });
  }

  // ---------- 8. Lenis smooth scroll — DISABLED ----------------------------
  // Native browser scroll is the right luxury default. Lenis intercepts the
  // wheel and animates it, which feels sticky/laggy with a mouse (especially
  // on macOS Magic Mouse + Logitech). Apple, Stripe, Le Labo, Aesop all use
  // native scroll. We follow.
  function initLenis() { /* intentionally no-op */ }

  // ---------- 9. GSAP reveal init -------------------------------------------

  function initReveals() {
    if (prefersReducedMotion()) {
      $$('[data-reveal]').forEach((el) => { el.style.opacity = '1'; el.style.transform = 'none'; });
      return;
    }
    if (!window.gsap) return;
    try {
      if (window.ScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);
      $$('[data-reveal]').forEach((el) => {
        window.gsap.fromTo(el,
          { opacity: 0, y: 30 },
          {
            opacity: 1, y: 0, duration: 1.2, ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 80%',
              once: true
            }
          }
        );
      });
    } catch (err) {
      console.warn('[YR reveals] init failed', err);
    }
  }

  // ---------- 11. Mobile sticky bottom CTA ----------------------------------

  function initStickyCTA() {
    if (!/^\/products\//.test(window.location.pathname)) return;

    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    const titleEl = document.querySelector('[data-product-title]') || document.querySelector('h1');
    const priceEl = document.querySelector('[data-product-price]');
    const submitBtn = productForm.querySelector('[type="submit"]');

    const title = titleEl ? titleEl.textContent.trim() : 'This tin';
    const price = priceEl ? priceEl.textContent.trim() : '';

    const bar = document.createElement('div');
    bar.className = 'yr-sticky-cta';
    bar.setAttribute('data-yr-sticky', '');
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = `
      <div class="yr-sticky-cta__top">
        <span class="yr-sticky-cta__price">${escapeHTML(price)}</span>
        <span class="yr-sticky-cta__title"><em>${escapeHTML(title)}</em></span>
      </div>
      <button type="button" class="yr-sticky-cta__btn" data-yr-sticky-add>Add to box</button>
    `;
    document.body.appendChild(bar);

    bar.querySelector('[data-yr-sticky-add]').addEventListener('click', (e) => {
      e.preventDefault();
      if (submitBtn) {
        submitBtn.click();
      } else {
        // Fallback: programmatic submit via Event so our submit handler fires
        productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });

    const hero = document.querySelector('[data-product-hero]') || document.querySelector('.product-hero') || document.querySelector('main');
    const threshold = () => (hero ? hero.getBoundingClientRect().bottom : 600);

    let visible = false;
    function onScroll() {
      const cartOpen = document.documentElement.getAttribute('data-cart-drawer-open') === 'true';
      const shouldShow = !cartOpen && threshold() < 0;
      if (shouldShow !== visible) {
        visible = shouldShow;
        bar.setAttribute('data-visible', shouldShow ? 'true' : 'false');
        bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Hide when cart drawer opens
    const obs = new MutationObserver(() => onScroll());
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-cart-drawer-open'] });

    onScroll();
  }

  // ---------- 11b. Mobile nav drawer focus trap -----------------------------
  // The mobile drawer markup + open/close is owned by sections/site-nav.liquid,
  // which toggles the `.is-open` class on [data-drawer]. We observe that class
  // here and layer on real modal semantics: focus in, Tab trap, page inert,
  // focus restore — mirroring the cart drawer pattern.

  function initMobileNavTrap() {
    const drawer = $('[data-drawer]');
    if (!drawer) return;
    const toggle = $('[data-nav-toggle]');
    const closeBtn = $('[data-drawer-close]', drawer);
    const trap = createFocusTrap(() => drawer);
    let lastFocused = null;
    let wasOpen = drawer.classList.contains('is-open');

    function onOpen() {
      lastFocused = toggle || document.activeElement;
      activatePageInert([drawer]);
      trap.activate();
      const target = (closeBtn && getFocusable(drawer).indexOf(closeBtn) !== -1)
        ? closeBtn
        : getFocusable(drawer)[0];
      if (target) target.focus();
    }

    function onClose() {
      trap.deactivate();
      releasePageInert();
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    const obs = new MutationObserver(() => {
      const isOpen = drawer.classList.contains('is-open');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) onOpen(); else onClose();
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // ---------- 12. Init on DOMContentLoaded ----------------------------------

  function init() {
    cart.render();
    cart.emitCountUpdate();
    initLenis();
    initReveals();
    initStickyCTA();
    initExpressCheckout();
    initMobileNavTrap();
    // Refresh against server in background to reconcile any drift
    cart.refresh().catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------- Expose --------------------------------------------------------
  window.YR = Object.assign({}, window.YR || {}, {
    cart: cart,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    moneyFormat: moneyFormat
  });
})();

/* build 2026-07-20b */
